import React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import type { TextResponse } from '@core-api/presentation/types/response';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { useOpenEndedPresentationContext } from '../../OpenEndedPresentationContext';
import { OpenEndedTitleDataHandler } from '../ui/OpenEndedTitleDataHandler';
import { SpeechBubbles } from './SpeechBubbles';
import { FlowingGrid } from './FlowingGrid';

interface VisualizationProps extends BoxT {
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  shouldHideResults: boolean;
  shouldShowUpvotes: boolean;
  onOpenOneByOneView: (index: number) => void;
  responses: TextResponse[];
  numberOfColumns: number;
}

export const ResponsesView = ({
  shouldShowUpvotes,
  responses,
  shouldHideResults,
  onOpenOneByOneView,
  CanvasVisualizationArea,
  numberOfColumns,
  ...rest
}: VisualizationProps) => {
  const { shouldAnimate, visualization, isReadonly, mode } =
    useOpenEndedPresentationContext();

  const shouldShowFlowingGrid =
    visualization === 'flowing_grid' && shouldAnimate;

  const ConditionalVisualizationWrapper = CanvasVisualizationArea ?? Box;

  return (
    <Box width="100%" height="100%">
      <OpenEndedTitleDataHandler showTextSizeDropdown={true} />
      <ConditionalVisualizationWrapper
        width="100%"
        flex="1 1 100%"
        p={1}
        extend={() => ({
          pointerEvents: 'auto',
        })}
      >
        <Box flex="1 1 auto" width="100%" height="100%" {...rest}>
          {shouldShowFlowingGrid ? (
            <FlowingGrid
              isResponsesHidden={shouldHideResults}
              showUpvotes={shouldShowUpvotes}
              responses={responses}
              onItemClick={onOpenOneByOneView}
              numberOfColumns={numberOfColumns}
              shouldAnimate={shouldAnimate}
              isReadonly={isReadonly}
            />
          ) : (
            <SpeechBubbles
              isResponsesHidden={shouldHideResults}
              showUpvotes={shouldShowUpvotes}
              responses={responses}
              onItemClick={onOpenOneByOneView}
              numberOfColumns={numberOfColumns}
              shouldPaginate={mode === 'live'}
              isReadonly={isReadonly}
              shouldAnimate={shouldAnimate}
            />
          )}
        </Box>
      </ConditionalVisualizationWrapper>
    </Box>
  );
};
